import React, { useState, useCallback } from 'react';
import { Pin, PinOff, Trash2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useFetchClientNotes,
  useCreateClientNote,
  useUpdateClientNote,
  useDeleteClientNote,
} from '@/hooks/useClientNotes';
import { formatDistanceToNow } from 'date-fns';

interface NotesTabProps {
  clientId: string;
}

export const NotesTab: React.FC<NotesTabProps> = ({ clientId }) => {
  const { data: notes = [], isLoading, error } = useFetchClientNotes(clientId);
  const createMutation = useCreateClientNote(clientId);
  const updateMutation = useUpdateClientNote(clientId);
  const deleteMutation = useDeleteClientNote(clientId);

  const [newNote, setNewNote] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    const trimmed = newNote.trim();
    if (!trimmed) return;
    createMutation.mutate(trimmed, {
      onSuccess: () => setNewNote(''),
    });
  }, [newNote, createMutation]);

  const handleTogglePin = useCallback(
    (noteId: string, currentPinned: boolean) => {
      updateMutation.mutate({ noteId, updates: { pinned: !currentPinned } });
    },
    [updateMutation]
  );

  const handleDelete = useCallback(
    (noteId: string) => {
      setDeleteTarget(noteId);
    },
    []
  );

  const confirmDelete = useCallback(() => {
    if (deleteTarget) {
      deleteMutation.mutate(deleteTarget);
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteMutation]);

  return (
    <div className="space-y-4">
      {/* Quick add */}
      <div className="bg-surface-1 border border-border rounded p-3 space-y-2">
        <Textarea
          placeholder="Add a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="bg-surface-2 border-border min-h-[72px] text-sm resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
        />
        <div className="flex justify-between items-center">
          <span className="text-[10px] text-muted-foreground font-label">
            {/(Mac|iPhone|iPad)/i.test((navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData?.platform ?? navigator.platform) ? 'Cmd' : 'Ctrl'}+Enter to save
          </span>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!newNote.trim() || createMutation.isPending}
            className="text-xs"
          >
            {createMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
            ) : null}
            Add Note
          </Button>
        </div>
      </div>

      {/* Notes list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : error ? (
        <p className="text-sm text-destructive py-4">Failed to load notes.</p>
      ) : notes.length === 0 ? (
        <div className="text-center py-8 space-y-1">
          <p className="text-sm text-muted-foreground">No notes yet.</p>
          <p className="text-xs text-muted-foreground">Add your first note above to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <div
              key={note.id}
              className={cn(
                'bg-surface-1 border rounded px-3 py-2.5 space-y-1',
                note.pinned ? 'border-primary/30' : 'border-border'
              )}
            >
              <p className="text-sm text-foreground whitespace-pre-wrap break-words">{note.content}</p>
              <div className="flex items-center justify-between pt-1">
                <span className="text-[10px] text-muted-foreground font-label">
                  {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleTogglePin(note.id, note.pinned)}
                    className={cn(
                      'p-2.5 rounded hover:bg-surface-3 transition-colors',
                      note.pinned ? 'text-primary' : 'text-muted-foreground'
                    )}
                    title={note.pinned ? 'Unpin note' : 'Pin note'}
                  >
                    {note.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="p-2.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    title="Delete note"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-surface-1 border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wider text-sm">
              Delete Note
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              This note will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
